import { useRef, useState } from "react";
import { EventSourceParserStream } from "eventsource-parser/stream";
import { jsonrepair } from "jsonrepair";

export function partialParse<T>(input: string) {
  try {
    return JSON.parse(input) as T;
  } catch (_) {
    try {
      return JSON.parse(jsonrepair(input)) as T;
    } catch (e) {
      console.error("Failed to repair JSON", e);
      return {} as T;
    }
  }
}

export function bodyToSseAsyncGenerator(
  body: NonNullable<Awaited<ReturnType<typeof fetch>>["body"]>
) {
  const eventSourceStream = body
    .pipeThrough(new TextDecoderStream())
    .pipeThrough(new EventSourceParserStream());

  return readableStreamToAsyncGenerator(eventSourceStream);
}

export async function* readableStreamToAsyncGenerator<T>(
  stream: ReadableStream<T>
) {
  const reader = stream.getReader();
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    yield value;
  }
}

export function safeJSONParse(json: string) {
  try {
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
}

export function useRefState<T>(initialValue: T) {
  const ref = useRef<T>(initialValue);
  const [state, setState] = useState<T>(initialValue);

  const setStateAndRef = (value: T | ((prev: T) => T)) => {
    const next =
      typeof value === "function"
        ? (value as (prev: T) => T)(ref.current)
        : value;

    ref.current = next;

    setState(next);
  };

  const getLatest = () => ref.current;

  return [state, setStateAndRef, getLatest] as const;
}
